import React, { useEffect, useCallback } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, Clock, Hash, TrendingUp, Repeat2, RefreshCw } from 'lucide-react';
import { PatientRecommendations, RecommendationItem } from '../../domain/PatientRecommendations';
import { getPatientRecommendations, refreshPatientRecommendations } from '../../application/patientRecommendations';
import ComponentCard from '../../../../shared/ui/common/ComponentCard';
import Button from '../../../../shared/ui/button/Button';
import { useSubscription } from "../../../subscription";

interface RecommendationsCardProps {
    patientId: string;
    hideHeader?: boolean;
    summaryOnly?: boolean;
    isCouple?: boolean;
}

const RecommendationsCard: React.FC<RecommendationsCardProps> = ({ patientId, hideHeader, summaryOnly, isCouple }) => {
    const { t } = useTranslation();
    const { isPremium } = useSubscription();
    const [recommendations, setRecommendations] = useState<PatientRecommendations | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [refreshing, setRefreshing] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const fetchRecommendations = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getPatientRecommendations(patientId, isCouple);
            setRecommendations(data);
        } catch {
            setError(t('recommendations.error'));
        } finally {
            setLoading(false);
        }
    }, [patientId, isCouple, t]);

    useEffect(() => {
        if (patientId && isPremium) {
            fetchRecommendations();
        } else {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patientId, isCouple, isPremium]);

    const handleRefresh = async () => {
        setRefreshing(true);
        setError(null);
        try {
            const data = await refreshPatientRecommendations(patientId, isCouple);
            setRecommendations(data);
        } catch {
            setError(t('recommendations.refreshError'));
        } finally {
            setRefreshing(false);
        }
    };

    if (!isPremium) {
        return (
            <ComponentCard
                title={
                    <span className="font-semibold text-lg flex items-center gap-2">
                        <span className="zen-text-gradient">{t("recommendations.title")}</span>
                    </span>
                }
            >
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                    <div className="w-16 h-16 ai-spectrum-fill rounded-full flex items-center justify-center mb-4 border border-accent/20 shadow-sm">
                        <Sparkles className="w-8 h-8 text-purple-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{t('subscription.restricted.title')}</h3>
                    <p className="text-gray-500 max-w-md mb-6">{t('subscription.restricted.description')}</p>
                    <Button
                        onClick={() => window.location.href = '/subscription'}
                        className="ai-spectrum-fill "
                    >
                        {t('subscription.status.upgrade')}
                    </Button>
                </div>
            </ComponentCard>
        );
    }

    if (loading && !recommendations) {
        return (
            <div className="space-y-4">
                <div className="h-28 bg-gray-50 dark:bg-white/[0.02] rounded-2xl animate-pulse" />
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    <div className="h-64 bg-gray-50 dark:bg-white/[0.02] rounded-2xl animate-pulse" />
                    <div className="h-64 bg-gray-50 dark:bg-white/[0.02] rounded-2xl animate-pulse" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="glass-effect glass-border border rounded-2xl p-8 text-center">
                <p className="text-red-500 mb-4">{error}</p>
                <Button onClick={fetchRecommendations} variant="outline">{t('recommendations.retry')}</Button>
            </div>
        );
    }

    if (!recommendations) {
        return (
            <div className="glass-effect glass-border border rounded-2xl p-8 text-center">
                <p className="text-gray-500">{t('recommendations.noRecommendations')}</p>
            </div>
        );
    }

    if (summaryOnly) {
        return (
            <div className="space-y-4">
                <p className="text-sm text-content-secondary italic leading-relaxed border-l-2 border-brand-200 pl-4 py-1">
                    "{recommendations.last_session_summary}"
                </p>
            </div>
        );
    }

    return (
        <div className={hideHeader ? "space-y-6" : "glass-effect glass-border border rounded-3xl p-8 shadow-theme-md space-y-6"}>
            {/* Header */}
            {!hideHeader && (
                <div className="flex justify-between items-center">
                    <span className="font-semibold text-lg flex items-center gap-2">
                        <span className="zen-text-gradient">
                            {t("recommendations.title")}
                        </span>
                    </span>
                    <button
                        onClick={handleRefresh}
                        disabled={refreshing}
                        className="flex items-center gap-1.5 text-xs text-content-tertiary hover:text-brand-500 transition-colors disabled:opacity-40"
                        title={t('recommendations.retry')}
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            )}

            {/* Last session summary */}
            <div className="rounded-xl border border-gray-100 dark:border-white/5 bg-gray-50/50 dark:bg-white/[0.02] p-5">
                <div className="flex items-center gap-2 mb-3">
                    <Clock className="w-4 h-4 text-content-tertiary" />
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('recommendations.lastSessionSummary')}</h4>
                    {recommendations.last_session_date && (
                        <span className="ml-auto text-xs text-content-tertiary">
                            {new Date(recommendations.last_session_date).toLocaleDateString()}
                        </span>
                    )}
                </div>
                <p className="text-sm text-content-secondary italic leading-relaxed border-l-2 border-gray-200 dark:border-gray-700 pl-4">
                    "{recommendations.last_session_summary}"
                </p>
            </div>

            {/* Two cards side by side */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {/* Suggested Techniques */}
                <SectionCard
                    icon={<TrendingUp className="w-4 h-4 text-purple-500" />}
                    iconBg="bg-purple-100 dark:bg-purple-900/30"
                    title={t('recommendations.suggestedTechniques')}
                    accentBorder="border-t-purple-400"
                    empty={recommendations.suggested_techniques.length === 0}
                    emptyText={t('recommendations.noneSuggested')}
                >
                    {recommendations.suggested_techniques.slice(0, 5).map((item, idx) => (
                        <TechniqueItem key={idx} item={item} index={idx} />
                    ))}
                </SectionCard>

                {/* Recurring Topics */}
                <SectionCard
                    icon={<Repeat2 className="w-4 h-4 text-orange-500" />}
                    iconBg="bg-orange-100 dark:bg-orange-900/30"
                    title={t('recommendations.recurringThemes')}
                    accentBorder="border-t-orange-400"
                    empty={recommendations.recurring_topics.length === 0}
                    emptyText={t('recommendations.noneRecorded')}
                >
                    {recommendations.recurring_topics.slice(0, 5).map((item, idx) => (
                        <TopicItem key={idx} item={item} index={idx} />
                    ))}
                </SectionCard>
            </div>

            <div className="text-xs text-content-tertiary flex items-center justify-end gap-2 pt-2 border-t border-gray-100 dark:border-white/5">
                <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                {t('recommendations.analysisBasedOn')} ({recommendations.last_session_date ? new Date(recommendations.last_session_date).toLocaleDateString() : 'N/A'})
            </div>
        </div>
    );
};

/* ─── Section card wrapper ─────────────────────────────────────────── */

function SectionCard({
    icon, iconBg, title, accentBorder, empty, emptyText, children,
}: {
    icon: React.ReactNode;
    iconBg: string;
    title: string;
    accentBorder: string;
    empty: boolean;
    emptyText: string;
    children: React.ReactNode;
}) {
    return (
        <div className={`glass-effect glass-border border border-t-2 ${accentBorder} rounded-2xl overflow-hidden flex flex-col`}>
            {/* Card header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 dark:border-white/5">
                <div className={`w-7 h-7 rounded-lg ${iconBg} flex items-center justify-center shrink-0`}>
                    {icon}
                </div>
                <h4 className="font-semibold text-sm text-gray-900 dark:text-gray-100">{title}</h4>
            </div>
            {/* Items */}
            <div className="flex-1 divide-y divide-gray-100 dark:divide-white/5">
                {empty ? (
                    <p className="text-sm text-content-tertiary italic px-5 py-6">{emptyText}</p>
                ) : children}
            </div>
        </div>
    );
}

/* ─── Technique item (number + title + description always visible) ─── */

const TECHNIQUE_COLORS = [
    "text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20",
    "text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/20",
    "text-accent-text bg-accent-tint",
    "text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20",
    "text-sky-600 dark:text-sky-400 bg-sky-50 dark:bg-sky-900/20",
];

const TechniqueItem: React.FC<{ item: RecommendationItem; index: number }> = ({ item, index }) => {
    const color = TECHNIQUE_COLORS[index % TECHNIQUE_COLORS.length];
    return (
        <div className="px-5 py-4 flex gap-3">
            <span className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center shrink-0 mt-0.5 ${color}`}>
                {index + 1}
            </span>
            <div className="flex-1 min-w-0">
                {item.title && (
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{item.title}</p>
                )}
                <p className="text-sm text-content-secondary leading-relaxed">{item.content}</p>
                {item.source_session_date && (
                    <div className="flex items-center gap-1 text-xs text-content-tertiary mt-2">
                        <Clock className="w-3 h-3" />
                        {new Date(item.source_session_date).toLocaleDateString()}
                    </div>
                )}
            </div>
        </div>
    );
};

/* ─── Topic item (plain: title + full description always visible) ───── */

const TopicItem: React.FC<{ item: RecommendationItem; index?: number }> = ({ item }) => {
    return (
        <div className="px-5 py-4 flex gap-3">
            <span className="w-5 h-5 rounded-full border-2 border-orange-300 dark:border-orange-700 flex items-center justify-center shrink-0 mt-0.5">
                <Hash className="w-2.5 h-2.5 text-orange-400 dark:text-orange-500" />
            </span>
            <div className="flex-1 min-w-0">
                {item.title && (
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{item.title}</p>
                )}
                <p className="text-sm text-content-secondary leading-relaxed">{item.content}</p>
                {item.source_session_date && (
                    <div className="flex items-center gap-1 text-xs text-content-tertiary mt-2">
                        <Clock className="w-3 h-3" />
                        {new Date(item.source_session_date).toLocaleDateString()}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RecommendationsCard;
